/**
 * URL utility functions for domain parsing and validation
 */

/**
 * Extract subdomain from a URL
 * Examples:
 *   - "acme.neurograph.io" → "acme"
 *   - "staging.acme.neurograph.io" → "staging.acme"
 *   - "neurograph.io" → null
 *
 * @param {string} url - The URL to parse
 * @param {string} baseDomain - The base domain (default: "neurograph.io")
 * @returns {string|null} The subdomain or null if none found
 */
export function extractSubdomain(url, baseDomain = 'neurograph.io') {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;

    // Remove base domain
    if (hostname.endsWith(baseDomain)) {
      const subdomain = hostname.slice(0, -(baseDomain.length + 1));
      return subdomain || null;
    }

    return null;
  } catch (error) {
    console.error('Error extracting subdomain:', error);
    return null;
  }
}

/**
 * Extract organization slug from query parameter
 * Examples:
 *   - "http://localhost:3000?org=acme" → "acme"
 *   - "http://localhost:3000?foo=bar&org=test" → "test"
 *
 * @param {string} url - The URL to parse
 * @param {string} paramName - The query parameter name (default: "org")
 * @returns {string|null} The organization slug or null if not found
 */
export function extractOrgFromQuery(url, paramName = 'org') {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get(paramName);
  } catch (error) {
    console.error('Error extracting org from query:', error);
    return null;
  }
}

/**
 * Extract organization slug from URL path
 * Examples:
 *   - "/workbench/acme" → "acme"
 *   - "/workbench/acme/dashboard" → "acme"
 *
 * @param {string} url - The URL to parse
 * @param {string} prefix - The path prefix (default: "/workbench/")
 * @returns {string|null} The organization slug or null if not found
 */
export function extractOrgFromPath(url, prefix = '/workbench/') {
  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    if (path.startsWith(prefix)) {
      const segments = path.slice(prefix.length).split('/');
      return segments[0] || null;
    }

    return null;
  } catch (error) {
    console.error('Error extracting org from path:', error);
    return null;
  }
}

/**
 * Validate domain format
 * Must be alphanumeric with hyphens and underscores
 * Length between 3-63 characters
 * Cannot start or end with hyphen
 *
 * @param {string} domain - The domain to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function isValidDomain(domain) {
  if (!domain || typeof domain !== 'string') {
    return false;
  }

  // Allow empty string (triggers default branding)
  if (domain === '') {
    return true;
  }

  // Check length
  if (domain.length < 3 || domain.length > 63) {
    return false;
  }

  // Check pattern: alphanumeric, hyphens, underscores
  // Cannot start or end with hyphen
  const pattern = /^[a-zA-Z0-9][a-zA-Z0-9_-]*[a-zA-Z0-9]$|^[a-zA-Z0-9]$/;
  return pattern.test(domain);
}

/**
 * Sanitize domain input
 * Converts to lowercase and removes invalid characters
 *
 * @param {string} domain - The domain to sanitize
 * @returns {string} The sanitized domain
 */
export function sanitizeDomain(domain) {
  if (!domain || typeof domain !== 'string') {
    return '';
  }

  // Convert to lowercase
  let sanitized = domain.toLowerCase().trim();

  // Remove invalid characters (keep only alphanumeric, hyphens, underscores)
  sanitized = sanitized.replace(/[^a-z0-9_-]/g, '');

  // Remove leading/trailing hyphens
  sanitized = sanitized.replace(/^-+|-+$/g, '');

  return sanitized;
}

/**
 * Get current URL (handles both browser and test environments)
 *
 * @returns {string} The current URL
 */
export function getCurrentUrl() {
  if (typeof window !== 'undefined' && window.location) {
    return window.location.href;
  }
  return '';
}
